import { Box, Button, Text } from "@dehub/theme";
import { CheckIcon, PencilSimpleLineIcon, XIcon } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ReasonTooltip } from "@/shared/a11y/reason-tooltip";
import classes from "../PartnerPage.module.css";

type Props = Readonly<{
  id: string;
  title: string;
  sub: string;
  editing: boolean;
  dirty: boolean;
  /** A save is running: Save shows its spinner and the buttons ignore clicks. */
  saving?: boolean;
  /** Shown under the header while editing (e.g. another section is incomplete). */
  notice?: ReactNode;
  /** Why the edit button is disabled; null when it is enabled. */
  disabledReason: ReactNode;
  onEdit: () => void;
  onCancel: () => void;
  children: ReactNode;
}>;

export function InfoCard({ id, title, sub, editing, dirty, saving = false, notice, disabledReason, onEdit, onCancel, children }: Props) {
  const { t } = useTranslation("partner");
  const { t: tCommon } = useTranslation("common");

  return (
    <Box
      component="section"
      id={id}
      aria-labelledby={`${id}-title`}
      className={`${classes.infoCard} ${editing ? classes.infoCardEditing : ""}`}
    >
      <Box className={classes.infoHead}>
        <Box>
          <Text id={`${id}-title`} component="h4" m={0} className={classes.infoTitle}>
            {title}
          </Text>
          <Text span display="block" className={classes.infoSub}>
            {sub}
          </Text>
        </Box>
        {editing ? (
          dirty ? (
            <Box className={classes.dirtyPill}>
              <Box component="span" className={classes.pillDot} />
              {t("unsaved")}
            </Box>
          ) : null
        ) : (
          <ReasonTooltip reason={disabledReason}>
            <Button
              data-edit-button=""
              variant="default"
              size="sm"
              radius="md"
              leftSection={<PencilSimpleLineIcon size={14} weight="bold" />}
              onClick={onEdit}
            >
              {tCommon("actions.edit")}
            </Button>
          </ReasonTooltip>
        )}
      </Box>

      {editing && notice ? notice : null}

      {children}

      {editing ? (
        <Box className={classes.infoFoot}>
          <Button
            variant="default"
            size="sm"
            radius="md"
            leftSection={<XIcon size={14} weight="bold" />}
            disabled={saving}
            onClick={onCancel}
          >
            {tCommon("actions.cancel")}
          </Button>
          <Button color="brand" type="submit" size="sm" radius="md" loading={saving} leftSection={<CheckIcon size={14} weight="bold" />}>
            {tCommon("actions.save")}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
